import {
  CellType,
  NUM_PHEROMONE_CHANNELS,
  NUM_REGISTERS,
  DEFAULT_CONFIG,
  type SwarmMap,
  type Ant,
  type World,
  type Program,
  type SimConfig,
  type SimResult,
} from './types';
import { PRNG } from './prng';
import { tickAnt, initPheroSparse, getPheroListLen } from './dsl-interpreter';
import { compile } from './bytecode';

export function createAnts(map: SwarmMap, count: number, rng: PRNG): Ant[] {
  const ants: Ant[] = [];
  for (let i = 0; i < count; i++) {
    ants.push({
      x: map.nestX,
      y: map.nestY,
      carrying: false,
      regs: new Array(NUM_REGISTERS).fill(0),
      pc: 0,
      tag: 0,
    });
  }
  return ants;
}

export function buildAntGrid(ants: Ant[], width: number, height: number): Uint8Array {
  const grid = new Uint8Array(width * height);
  for (let i = 0; i < ants.length; i++) {
    grid[ants[i].y * width + ants[i].x]++;
  }
  return grid;
}

export function createWorld(map: SwarmMap, program: Program, config: SimConfig = DEFAULT_CONFIG): World {
  const rng = new PRNG(map.seed);
  const ants = createAnts(map, config.antCount, rng);
  const size = map.width * map.height;
  return {
    map,
    ants,
    tick: 0,
    foodCollected: 0,
    program,
    bytecode: compile(program),
    antGrid: buildAntGrid(ants, map.width, map.height),
    rngState: rng.state,
    pheroActive: new Uint8Array(size),
    pheroList: new Uint32Array(size),
    pheroListLen: 0,
  };
}

const _rng = new PRNG(1);

export function stepTick(world: World, config: SimConfig = DEFAULT_CONFIG): void {
  const { map, ants, bytecode, antGrid } = world;
  const instrCount = world.program.instructions.length;
  _rng.state = world.rngState;

  initPheroSparse(world.pheroActive, world.pheroList, world.pheroListLen);

  for (let i = 0; i < ants.length; i++) {
    const ant = ants[i];
    const delivered = tickAnt(ant, i, bytecode, instrCount, map, ants, _rng, config.maxOpsPerTick, antGrid, config.senseRange);
    if (delivered) world.foodCollected++;
    map.visitCounts[ant.y * map.width + ant.x]++;
  }

  world.pheroListLen = decayPheromones(map, world.pheroList, world.pheroActive, getPheroListLen());
  world.rngState = _rng.state;
  world.tick++;
}

function decayPheromones(map: SwarmMap, list: Uint32Array, active: Uint8Array, len: number): number {
  const pher = map.pheromones;
  let newLen = 0;
  for (let li = 0; li < len; li++) {
    const ci = list[li];
    if (!active[ci]) continue;
    const base = ci * NUM_PHEROMONE_CHANNELS;
    let anyNZ = false;
    for (let ch = 0; ch < NUM_PHEROMONE_CHANNELS; ch++) {
      const v = pher[base + ch];
      if (v > 0) {
        pher[base + ch] = v - 1;
        if (v > 1) anyNZ = true;
      }
    }
    if (anyNZ) {
      list[newLen++] = ci;
    } else {
      active[ci] = 0;
    }
  }
  return newLen;
}

export function runSimulation(
  map: SwarmMap,
  program: Program,
  config: SimConfig = DEFAULT_CONFIG,
  onTick?: (world: World) => void,
): SimResult {
  const world = createWorld(map, program, config);

  for (let t = 0; t < config.maxTicks; t++) {
    stepTick(world, config);
    if (onTick) onTick(world);
  }

  return {
    foodCollected: world.foodCollected,
    totalFood: map.totalFood,
    ticks: world.tick,
    mapSeed: map.seed,
    mapName: map.name,
  };
}

export function cloneMap(map: SwarmMap): SwarmMap {
  return {
    ...map,
    cells: new Uint8Array(map.cells),
    food: new Uint16Array(map.food),
    pheromones: new Uint16Array(map.pheromones),
    visitCounts: new Uint32Array(map.visitCounts),
  };
}

export function createEmptyMap(width: number, height: number, seed: number, name: string): SwarmMap {
  const size = width * height;
  const cells = new Uint8Array(size);
  const food = new Uint16Array(size);
  const pheromones = new Uint16Array(size * NUM_PHEROMONE_CHANNELS);

  const nestX = Math.floor(width / 2);
  const nestY = Math.floor(height / 2);
  cells[nestY * width + nestX] = CellType.NEST;

  const visitCounts = new Uint32Array(size);
  return { width, height, cells, food, pheromones, visitCounts, nestX, nestY, totalFood: 0, seed, name };
}
